import {Plan} from 'types';
import {ValidationMessage, MessageOpts} from '../../types';

type Interval = { min: number; max: number };
type AxisRect = { x: Interval; y: Interval };
type RectWithId = { id: string; rect: AxisRect };

const overlapLen = (a: Interval, b: Interval) => Math.min(a.max, b.max) - Math.max(a.min, b.min);

type ArrangementSummary = { ok: boolean; overlappingPairs: string[]; hasMixed: boolean };

function validateArrangementFromRects(items: RectWithId[]): ArrangementSummary {
    if (!Array.isArray(items) || items.length < 2) return {ok: true, overlappingPairs: [], hasMixed: false};
    const pairs: string[] = [];
    let anyX = false, anyY = false;
    for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
            const A = items[i].rect, B = items[j].rect;
            const xOverlap = overlapLen(A.x, B.x) > 0;
            const yOverlap = overlapLen(A.y, B.y) > 0;
            if (xOverlap && yOverlap) pairs.push(`${items[i].id}–${items[j].id}`);
            if (xOverlap) anyX = true;
            if (yOverlap) anyY = true;
        }
    }
    return {
        ok: pairs.length === 0 && !(anyX && anyY),
        overlappingPairs: pairs,
        hasMixed: pairs.length === 0 && anyX && anyY,
    };
}

function arrangementMessagesFromSummary(
    summary: ArrangementSummary,
    opts: MessageOpts = {}
): ValidationMessage[] {
    const {
        resolveLocation,
        resolveLocationText,
        severity = 'error',
    } = opts;

    if (summary.ok) return [];

    const pairMsg = summary.overlappingPairs.length > 0
        ? `Die Einstiegsöffnungen liegen zu nahe beieinander. Sperrbereiche überlappen.`
        : '';
    const mixedMsg = summary.hasMixed
        ? `Unzulässige Anordnung der Öffnungen: Sperrbereiche dürfen sich nur entlang einer Achse (X oder Y), nicht `
        + `jedoch in beiden, überlagern.`
        : '';

    const mk = (message: string): ValidationMessage => ({
        message,
        severity,
        ...(resolveLocation ? {resolveLocation} : {}),
        ...(resolveLocationText ? {resolveLocationText} : {}),
    });


    const out: ValidationMessage[] = [];
    if (pairMsg) out.push(mk(pairMsg));
    if (mixedMsg) out.push(mk(mixedMsg));
    return out;

}

const rectForbidden = (x: number, y: number, width: number, height: number, marginFactor = 1 / 3): AxisRect => {
    const mx = width * marginFactor;
    const my = height * marginFactor;
    return {x: {min: x - mx, max: x + width + mx}, y: {min: y - my, max: y + height + my}};
}

const circleForbidden = (cx: number, cy: number, diameter: number, marginFactor = 1 / 3): AxisRect => {
    const r = diameter / 2;
    const m = (diameter * marginFactor);
    const ext = r + m;
    return {x: {min: cx - ext, max: cx + ext}, y: {min: cy - ext, max: cy + ext}};
}

// ---------- ID set helpers for UI highlighting ----------
export type LidOpeningErrorSets = {
    manholeIds: Set<string>;
    rectIds: Set<string>;
    circIds: Set<string>;
}

export const getLidOpeningErrorSets = (plan: Plan): LidOpeningErrorSets => {
    const manholes: any[] = (plan as any)?.lid?.manholes ?? [];
    const rects = (plan as any)?.lid?.blockOuts ?? [];
    const circs = (plan as any)?.lid?.circularBlockOuts ?? [];
    const rectMarginFactor = plan.lid?.type == 'A15' || plan.lid?.type == 'B125' ? 1/5 : 1/3
    const circMarginFactor = 1/3

    const items: (RectWithId & { kind: 'm' | 'r' | 'c'; rawId: string })[] = [
        ...manholes.map((m: any, idx: number) => ({
            id: m.id ?? `m${idx}`,
            rawId: m.id ?? `m${idx}`,
            kind: 'm',
            rect: rectForbidden(m.x, m.y, m.width, m.height, rectMarginFactor)
        })),
        ...rects.map((b: any, idx: number) => ({
            id: b.id ?? `b${idx}`,
            rawId: b.id ?? `b${idx}`,
            kind: 'r',
            rect: rectForbidden(b.x, b.y, b.width, b.height, rectMarginFactor)
        })),
        ...circs.map((c: any, idx: number) => ({
            id: c.id ?? `c${idx}`,
            rawId: c.id ?? `c${idx}`,
            kind: 'c',
            rect: circleForbidden(c.x, c.y, c.diameter, circMarginFactor)
        })),
    ];

    const summary = validateArrangementFromRects(items);
    const manholeIds = new Set<string>();
    const rectIds = new Set<string>();
    const circIds = new Set<string>();

    if (summary.ok) return {manholeIds, rectIds, circIds};

    const addById = (id: string) => {
        const it = items.find(i => i.id === id);
        if (!it) return;
        if (it.kind === 'm') manholeIds.add(it.rawId);
        if (it.kind === 'r') rectIds.add(it.rawId);
        if (it.kind === 'c') circIds.add(it.rawId);
    }

    if (summary.hasMixed) {
        items.forEach(i => addById(i.id));
        return {manholeIds, rectIds, circIds};
    }

    // Only area-overlap pairs
    const pairIds = new Set<string>();
    summary.overlappingPairs.forEach(p => {
        const [a, b] = p.split('–');
        if (a) pairIds.add(a);
        if (b) pairIds.add(b);
    });
    pairIds.forEach(addById);
    return {manholeIds, rectIds, circIds};
}

export type IntermediateCeilingOpeningErrorSets = {
    rectIds: Set<string>;
    circIds: Set<string>;
}

export const getIntermediateCeilingOpeningErrorSets = (plan: Plan): IntermediateCeilingOpeningErrorSets => {
    const rects = (plan as any)?.intermediateCeiling?.blockOuts ?? [];
    const circs = (plan as any)?.intermediateCeiling?.circularBlockOuts ?? [];
    const rectMarginFactor = 1/5
    const circMarginFactor = 1/3

    const items: (RectWithId & { kind: 'r' | 'c'; rawId: string })[] = [
        ...rects.map((b: any, idx: number) => ({
            id: b.id ?? `b${idx}`,
            rawId: b.id ?? `b${idx}`,
            kind: 'r',
            rect: rectForbidden(b.x, b.y, b.width, b.height, rectMarginFactor)
        })),
        ...circs.map((c: any, idx: number) => ({
            id: c.id ?? `c${idx}`,
            rawId: c.id ?? `c${idx}`,
            kind: 'c',
            rect: circleForbidden(c.x, c.y, c.diameter, circMarginFactor)
        })),
    ];

    const summary = validateArrangementFromRects(items);
    const rectIds = new Set<string>();
    const circIds = new Set<string>();
    if (summary.ok) return {rectIds, circIds};

    const addById = (id: string) => {
        const it = items.find(i => i.id === id);
        if (!it) return;
        if (it.kind === 'r') rectIds.add(it.rawId);
        if (it.kind === 'c') circIds.add(it.rawId);
    }
    if (summary.hasMixed) {
        items.forEach(i => addById(i.id));
        return {rectIds, circIds};
    }
    const pairIds = new Set<string>();
    summary.overlappingPairs.forEach(p => {
        const [a, b] = p.split('–');
        if (a) pairIds.add(a);
        if (b) pairIds.add(b);
    });
    pairIds.forEach(addById);
    return {rectIds, circIds};
}

export const getLidOpeningsArrangementMessages = (
    plan: Plan,
    opts: MessageOpts = {}
): ValidationMessage[] => {
    const manholes: any[] = (plan as any)?.lid?.manholes ?? [];
    const rectMarginFactor = plan.lid?.type == 'A15' || plan.lid?.type == 'B125' ? 1/5 : 1/3
    const circMarginFactor = 1/3

    const rects: RectWithId[] = [
        ...manholes.map((m: any, idx: number) => ({
            id: m.id ?? `m${idx}`,
            rect: rectForbidden(m.x, m.y, m.width, m.height, rectMarginFactor)
        })),
        ...(((plan as any)?.lid?.blockOuts ?? []).map((b: any, idx: number) => ({
            id: b.id ?? `b${idx}`,
            rect: rectForbidden(b.x, b.y, b.width, b.height, rectMarginFactor)
        }))),
        ...(((plan as any)?.lid?.circularBlockOuts ?? []).map((c: any, idx: number) => ({
            id: c.id ?? `c${idx}`,
            rect: circleForbidden(c.x, c.y, c.diameter, circMarginFactor)
        }))),
    ];
    const summary = validateArrangementFromRects(rects);
    return arrangementMessagesFromSummary(summary, opts);
}

export const getIntermediateCeilingOpeningsArrangementMessages = (
    plan: Plan,
    opts: MessageOpts = {}
): ValidationMessage[] => {
    const rectMarginFactor = 1/5
    const circMarginFactor = 1/3

    const rects: RectWithId[] = [
        ...(((plan as any)?.intermediateCeiling?.blockOuts ?? []).map((b: any, idx: number) => ({
            id: b.id ?? `b${idx}`,
            rect: rectForbidden(b.x, b.y, b.width, b.height, rectMarginFactor)
        }))),
        ...(((plan as any)?.intermediateCeiling?.circularBlockOuts ?? []).map((c: any, idx: number) => ({
            id: c.id ?? `c${idx}`,
            rect: circleForbidden(c.x, c.y, c.diameter, circMarginFactor)
        }))),
    ];
    const summary = validateArrangementFromRects(rects);
    return arrangementMessagesFromSummary(summary, opts);
}
